<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Chart of accounts</h1>
            <p class="text-muted mb-0">
              The accounts your transactions and journal entries are posted to
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton
              v-if="!items.length && !loading && !error"
              color="light"
              class="mr-2"
              :disabled="creatingDefault"
              @click="createDefault"
            >
              Use default accounts
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add account
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <CInput
          v-if="items.length"
          v-model="search"
          placeholder="Search name, account no. or category"
          aria-label="Search accounts"
          class="mb-3"
        >
          <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
        </CInput>

        <!-- First load -->
        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
          <div class="text-muted mt-2">Loading accounts…</div>
        </div>

        <!-- Error -->
        <div v-else-if="error && !items.length" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
          <h2 class="h6 font-weight-bold">Couldn't load your accounts</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Retry
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-list" size="xl" class="text-muted mb-2" />
          <h2 class="h6 font-weight-bold">No accounts yet</h2>
          <p class="text-muted">
            Start with the default Malaysian chart of accounts, or add your own.
          </p>
          <CButton
            color="light"
            class="mr-2"
            :disabled="creatingDefault"
            @click="createDefault"
          >
            Use default accounts
          </CButton>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add account
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!filteredItems.length" class="text-center p-4">
          <h2 class="h6 font-weight-bold">No accounts match your search</h2>
          <CButton color="light" @click="search = ''">
            <CIcon name="cil-x" /> Clear search
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="filteredItems"
              :fields="fields"
              :items-per-page="25"
              items-per-page-select
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #accountNo="{ item }">
                <td class="text-monospace">{{ item.accountNo }}</td>
              </template>
              <template #actions="{ item }">
                <td class="py-2 text-right text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="`Edit ${item.name}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="`Delete ${item.name}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" class="text-danger" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div
              v-for="item in filteredItems"
              :key="item.id"
              class="border rounded p-3 mb-2 d-flex justify-content-between align-items-start"
            >
              <div>
                <div class="font-weight-bold">{{ item.name }}</div>
                <div class="small text-muted">
                  <span class="text-monospace">{{ item.accountNo }}</span>
                  · {{ item.accountTypeDescription }}
                </div>
                <div v-if="item.category" class="small text-muted">
                  {{ item.category }}
                </div>
              </div>
              <div class="text-nowrap">
                <CButton
                  color="light"
                  class="mr-1"
                  :aria-label="`Edit ${item.name}`"
                  @click="onEdit(item)"
                >
                  <CIcon name="cil-pencil" />
                </CButton>
                <CButton
                  color="light"
                  :aria-label="`Delete ${item.name}`"
                  @click="showDeleteConfirmation(item)"
                >
                  <CIcon name="cil-trash" class="text-danger" />
                </CButton>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirm -->
    <CModal :show.sync="warningModal" :close-on-backdrop="!deleting" centered>
      <template #header>
        <h5 class="modal-title">Delete account {{ itemToDelete.accountNo }}?</h5>
      </template>
      <p class="mb-0">
        “{{ itemToDelete.name }}” will be removed from your chart of accounts.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="doDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />Delete account
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ChartOfAccountApi from "@/lib/chartOfAccountApi";

export default {
  name: "ChartOfAccountList",
  data() {
    return {
      items: [],
      fields: [
        { key: "accountNo", label: "Account no." },
        { key: "name", label: "Name" },
        { key: "accountTypeDescription", label: "Type" },
        { key: "category", label: "Category" },
        { key: "actions", label: "", _style: "width:1%", sorter: false },
      ],
      search: "",
      loading: true,
      error: false,
      creatingDefault: false,
      deleting: false,
      api: new ChartOfAccountApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  computed: {
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.items;
      return this.items.filter((i) =>
        [i.name, i.accountNo, i.category, i.accountTypeDescription]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Accounting", "Chart of accounts"]);
    this.refreshTable();
  },
  methods: {
    refreshTable() {
      this.loading = true;
      this.error = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error(err);
          this.error = true;
          if (this.items.length) {
            this.$toast.error(
              "Couldn't refresh your accounts. Check your connection and try again."
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/ChartOfAccount/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    doDelete() {
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Account deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't delete the account. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    createDefault() {
      this.creatingDefault = true;
      this.api
        .createDefaultData()
        .then(() => {
          this.$toast.success("Default accounts added");
          this.refreshTable();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(
            `Couldn't add the default accounts. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          this.creatingDefault = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/ChartOfAccount" });
    },
  },
};
</script>
